/*
 * Authors: mariushirjanu
 * Created: 20/10/2021
 */

import { ICodeDescription, ICodeValue, ICompany, ICompanyCountry, IContact, ICountry, ILabelledValue, ILocation, IValueObject, IWebshop } from '../../../../type_definitions/datatypes';

import { HttpDataService, IHttpDataCache } from '../../../shared/http/http-data.service';
import { Injectable } from '@angular/core';

import { RoleContactType } from '../../../shared/contact/role-contact-type';

export interface IRoleContact {
  contact:IContact;
  contactType?:RoleContactType;
  creationDate?:number;
  creationUserName?:string;
}

export interface IWebshopInfo {
  customerCompany:string;
  customerMainContact:IContact;
  customerCompanyHQ:ILocation;
  addressHQ:ILocation;
  customerSector:ICodeDescription;
  customerSectorComment?:string;
  customerHomepage:string;
  webshopName:string;
  inProduction:boolean;
  isMainWebshop:boolean;
  webshopDescription:string;
  webshopType:ICodeValue;
  orderProcess:ICodeValue;
  productProcurement:boolean;
  localServiceRFP:boolean;
  opportunityType:ICodeValue;
  winChances:ICodeValue;
  competitors:string;
  incumbentOemPartner:string;
  detractors:string;
  expectedDecisionDate:string;
  expectedStartDate:string;
  expectedOrdersPerYear:number;
  revenueForecast:number;
  oemCatalogAvailable:ILabelledValue;
  bpCatalogActivatedQty:number;
  bpCatalogNotActivatedQty:number;
  expectedCatalogUploadDate:string;
  catalogUploadComment:string;
  userAddressListAvailable:ILabelledValue;
  expectedUserUploadDate:string;
  userListUploadComment:string;
  webshopStatus:ICodeValue;
  webshopStatusDate:number | Date;
  webshopStatusComments:string;
  webshopLastUpdateComments:number | Date;
  amTime:string;
  amType:ICodeValue;
  orderManagementType:ICodeValue;
  orderUpdateRoleContacts:IRoleContact[];
  orderUpdateContacts:IContact[];
  focusContactType:ICodeValue;
  focusContact:IRoleContact[];
  timeSlot:string;
  gbdm:IContact;
  gbdmCompany:string;
  lamCompanyCountry:ICompanyCountry;
  lamCompanyDepartment:ICompanyCountry;
  lamCustomerCountry:ICountry;
  lamType:IValueObject;
  lamCustomerRelation:ICodeValue;
  lamComments:string;
  activeCustomers:number;
  totalCustomers:number;
  superLAM:IContact;
  lamRoleContacts:IRoleContact[];
  lamMainContacts:IContact[];
  oemRoleContacts:IRoleContact[];
  oemMainContacts:IContact[];
  isMainOem:boolean;
  oem:ICompany;
  customerIdWithOem:string;
  oemCustomerType:ICodeDescription;
  expectedDeskTransferDate:string;
  spotlight:boolean;
}

export interface ICustomerWebshopInfo {
  company:ICompany;
  mainContact:IRoleContact;
  companyHQ:ILocation;
  homepage:string;
  hasCustomerLogo:boolean;
  spotlight:boolean;
  hasWebshopLogo:boolean;
}

interface IOpportunityAssessment {
  opportunity:ICodeValue;
  winChances:ICodeValue;
  competitors:string;
  incumbentOemPartner:string;
  detractors:string;
  expectedDecisionDate:string;
  expectedStartDate:string;
  expectedOrdersPerYear:number;
  revenueForecastUSD:number;
  catalogUploadComment:string;
  userListAvailable:ILabelledValue;
  expectedUserListUploadDate:string;
  userListUploadComment:string;
}

export interface IAccountManagement {
  amTime:string;
  timeSlot:string;
  accountManagementType:ICodeValue;
  focusContactType:ICodeValue;
  orderManagementType:ICodeValue;
  orderUpdateRoleContacts:IRoleContact[];
}

export interface IOemWebshopInfo {
  mainOem:boolean;
  company:ICompany;
  roleContacts:IRoleContact[];
  customerIdWithOem:string;
  customerType:ICodeDescription;
}

export interface ICatalogWebshopInfo {
  expectedCatalogUploadDate:string;
  expectedCatalogUpdateDate: string;
  expectedDecisionDate: string;
  catalogUploadComment: string;
  bpCatalogActivatedQty: number;
  bpCatalogNotActivatedQty: number;
  oemCatalogAvailable: ILabelledValue;
}

export interface ILamWebshopInfo {
  oemCompanyDepartment?:ICompanyCountry;
  bpOrCustomerCompanyCountry?:ICompanyCountry;
  type:string | IValueObject;
  roleContacts:IRoleContact[];
  customerRelation:ICodeValue;
  comments:string;
  activeCustomers:number;
  totalCustomers:number;
  superLAM:IContact;
}

export interface IWebshopInfoData {
  customerInfo:ICustomerWebshopInfo;
  webshop:IWebshop;
  opportunityAssessment:IOpportunityAssessment;
  accountManagement:IAccountManagement;
  oemInfo:IOemWebshopInfo;
  catalogInfo: ICatalogWebshopInfo;
  lam:ILamWebshopInfo;
  gbdmInfo:{
    webshopContact:IRoleContact;
    expectedDeskTransferDate:string;
  };
}

@Injectable()
export class WebshopInfoDataService {
  private cache:IHttpDataCache<any>;
  private runningRequests:string[] = [];

  constructor(
    private httpDataService:HttpDataService
  ) {
    this.cache = httpDataService.createCache();
  }

  public getWebshopInfo(webshopId:number):Promise<IWebshopInfoData> {
    const requestId = `WEBSHOP-INFO-${webshopId}`;
    return this.httpDataService.doGET<IWebshopInfoData>(
      `rest/webshops/${webshopId}/info`,
      undefined,
      undefined,
      'Error loading webshop info',
      requestId
    ).finally(() => {
      this.runningRequests.unshift(requestId);
    });
  }

  public saveWebshopInfo(webshopId:number, data:IWebshopInfoData):Promise<IWebshopInfoData> {
    return this.httpDataService.doPUT(
      `rest/webshops/${webshopId}/info`,
      undefined,
      data,
      undefined,
      'Error saving webshop info'
    );
  }

  public getFocusContactTypes():Promise<ICodeValue[]> {
    return this.cache.doGET(
      'rest/webshops/focuscontacttypes',
      undefined,
      undefined,
      'Error loading types'
    );
  }

  public getFocusContacts(webshopId:number):Promise<IRoleContact[]> {
    const requestId = `WEBSHOP-INFO-FC-${webshopId}`;
    return this.httpDataService.doGET<IRoleContact[]>(
      `rest/webshops/${webshopId}/focuscontacts`,
      undefined,
      undefined,
      'Error loading contacts',
      requestId
    ).finally(() => {
      this.runningRequests.unshift(requestId);
    });
  }

  public clear():void {
    this.runningRequests.forEach(requestId => {
      this.httpDataService.cancelRequests(requestId);
    });
    this.runningRequests = [];
  }
}